/***
 * Created on 2025/1/6
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useState} from 'react';
import {I18nextProvider, useTranslation} from "react-i18next";
import sharedI18n from "common/SharedI18n";
import {QueryClient, QueryClientProvider, useMutation, useQuery} from "@tanstack/react-query";
import {useOnChange} from "common/Hook";
import {getErrorMessage} from 'common/ServiceCommon';
import {apiGet, apiPost} from "../../service/api";
import {useCountdown} from "../../hook/countdown";
import Captcha from "../common/captcha";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false, // default: true
        },
    },
});

function SamlCreateAccount(props) {
    const { t } = useTranslation();
    const url = new URL(location.href);
    const {seconds, setSeconds} = useCountdown(0);
    const {values, onChange, setValues} = useOnChange({code: ''});
    const [errorMsg, setErrorMsg] = useState('');

    const {data, isSuccess} = useQuery({
        queryKey: ['get_countdown'],
        queryFn: async () => {
            const data = await apiGet('/ajax/countdown');
            if (data?.countdown) {
                const now = new Date().getTime();
                const value = Math.round((now - data?.countdown) / 1000) || 0;
                setSeconds(value > 60 ? 0 : 60 - value);
            }
            else {
                setSeconds(0);
            }
            return data;
        }
    });

    const resendMutation = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/saml/send_verify_email', data),
        onSuccess: data => {
            queryClient.invalidateQueries({queryKey: ['get_countdown']});
        },
        onError: (error, variables, context) => {}
    });

    const onResend = async (e) => {
        const data = {}
        await resendMutation.mutateAsync(data);
    };

    const createMutation = useMutation({
        mutationFn: async (data) => await apiPost(`/ajax/saml/verify_email_code`, data),
        onSuccess: (data) => {
            const state = url.searchParams.get('state');
            let link = `/invitation-signup/${data.invitation_id}`;
            if (state) {
                link = `${link}?state=${state}`;
            }
            location.href = link;
        },
        onError: (error, variables, context) => {
            queryClient.invalidateQueries({queryKey: ['get_captcha_image']});
            if (error?.cause?.code === 1006003) {
                setErrorMsg(getErrorMessage(t, 1004004));
            }
        }
    });

    const onProceed = async (e) => {
        let data = {
            code: values.code,
        }
        if (values.captcha) {
            data.captcha = values.captcha;
        }
        await createMutation.mutateAsync(data);
    }

    const onCancel = (e) => {
        const state = url.searchParams.get('state');
        location.href = state ? state : '/account';
    }

    return (
        <section className="common-page-wrapper">
            <div className="common-page-header">{t('@Create Your QNAP ID@')}</div>
                <div className="common-page-body">
                    <p>{t('@We have detected that your email ({{retrieved_email}}) is not yet registered as a QNAP ID.@', {
                        retrieved_email: url.searchParams.get('email')
                    })}</p>
                    <p>{t('@To continue using QNAP services via {{idp_name}}, you need to create a QNAP ID. Your email will be used as your QNAP ID.@', {
                        idp_name: url.searchParams.get('saml_sso_id')
                    })}</p>
                    <p>{t('@We have sent a verification code to your email. Please enter the code below to verify your identity.@')}</p>

                    <div className="common-form-group">
                        <div className="comp-input">
                            <div className="input-field--hybrid">
                                <input type="text" name="code" value={values.code} onChange={onChange}
                                       className={`${errorMsg ? 'input--invalid' : ''}`}
                                       placeholder="Verification Code"/>
                                <button className="q-btn q-btn-default" disabled={seconds>0} onClick={onResend}>{t('@Resend@')}  {seconds <= 0 ? '' : `${seconds}`}</button>
                            </div>
                        </div>
                        <p className={`text-error mt-10 ${errorMsg ? '' : 'hide'}`}>{errorMsg}</p>
                    </div>

                    <Captcha value={values.captcha} inputChangeHandler={onChange} resetValue={() => setValues(prev => ({...prev, captcha: ''}))}/>
                </div>

                <div className="common-page-footer">
                    <div className="q-btn-group btn-align-right">
                        <button
                            className="q-btn q-btn--primary btn-medium btn-rounded-normal"
                            onClick={async (e) => await onProceed()}
                            disabled={values.code.length <= 0}>
                            {t('@Verify@')}</button>
                        <button className="q-btn q-btn--default btn-medium btn-rounded-normal" onClick={onCancel}>{t('@Cancel@')}</button>
                    </div>
                </div>
        </section>
    );
}

const SamlCreateAccountIndex = () => {
    return (
        <I18nextProvider i18n={sharedI18n}>
            <QueryClientProvider client={queryClient}>
                <SamlCreateAccount/>
            </QueryClientProvider>
        </I18nextProvider>
    )
}

export default SamlCreateAccountIndex;